import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Row,
  Col,
  Form,
  Loader,
  Iconography,
  Tooltip,
  DatePicker,
} from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import type { OrderDetailsResponse } from '../../../../../api/services/lupp/order/dto.order';
import { usePatchOrderDetails } from '../../../../../api/services/lupp/order/order';
import { useStore } from '../../../../../store/store';
import {
  formatDate,
  formatToUTCDate,
  getContentfulLocaleFromLanguage,
  convertToContentfulLocale,
} from '../../../../../utils/tools';
import { createRegistrationFormFields } from './formFields';
import {
  SUCCESS_MESSAGE_DURATION_MS,
  DEFAULT_SAVE_ERROR_MESSAGE,
  getLocalizedErrorMessage,
} from './constants';
import { StatusMessage } from './StatusMessage/StatusMessage';
import styles from './Registration.module.scss';

type RegistrationProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * Form data structure for vehicle registration and delivery details
 *
 * @typedef {Object} RegistrationFormDefaultValues
 * @property {string} [firstRegistrationDate] - Date of the vehicle's first registration (ISO 8601 format)
 * @property {string} [licensePlateNumber] - License plate number assigned to the vehicle
 * @property {string} [registrationStatus] - Current status of the vehicle registration process
 * @property {string} [scheduledDeliveryDate] - Scheduled date for vehicle delivery (ISO 8601 format)
 * @property {string} [scheduledDeliveryTime] - Scheduled time for vehicle delivery (12-hour format, e.g., "2PM")
 */
export type RegistrationFormDefaultValues = {
  firstRegistrationDate?: string;
  licensePlateNumber?: string;
  registrationStatus?: string;
  scheduledDeliveryDate?: string;
  scheduledDeliveryTime?: string;
  licensePlateIssuanceCode?: string;
  registrationCode1?: string;
  registrationCode2?: string;
  registrationOrderOtt?: string;
};

/**
 * Registration Details component
 * Displays vehicle readiness information and registration/delivery forms
 *
 * @component
 * @param {RegistrationProps} props - Component props
 * @returns {JSX.Element} Registration details with forms
 * @example
 * <Registration orderData={orderData} theme="light" labels={labels} />
 */
const Registration: React.FC<RegistrationProps> = ({
  orderData,
  theme,
  labels,
  dataTestId = 'registration-details',
}) => {
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const { userIdentity } = useUserIdentity();
  const isNL = userIdentity?.countryCode === 'NL';
  // Date format follows the explicit language override when set, otherwise the active store
  // locale (e.g. 'de-de' -> 'de-DE'); country is not used here since it can differ from locale
  const languageBasedLocale = useMemo(
    () =>
      language ? getContentfulLocaleFromLanguage(language) : convertToContentfulLocale(locale),
    [language, locale]
  );
  const formRef = useRef<FormRef>(null);
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [agencyPdiRoadworthinessCheckDateValue, setAgencyPdiRoadworthinessCheckDateValue] =
    useState<string | null>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Patch order details mutation
  const patchOrderDetailsMutation = usePatchOrderDetails();
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Use retail delivery data from the main order response
  const registrationDeliveryDetails = orderData.retailDeliveryDetails;

  const { agencyPdiRoadworthinessCheckDate } =
    registrationDeliveryDetails?.vehicleReadiness || orderData.vehicleReadinessDetails || {};

  const vehicleEtaAtDeliveryCenter = orderData.vehicleEtaAtDeliveryCenter ?? null;

  useEffect(() => {
    setAgencyPdiRoadworthinessCheckDateValue(agencyPdiRoadworthinessCheckDate ?? null);
  }, [agencyPdiRoadworthinessCheckDate]);

  const formFields = useMemo(() => {
    const defaultValues = {
      firstRegistrationDate:
        registrationDeliveryDetails?.firstRegistrationDetails?.firstRegistrationDate ?? undefined,
      licensePlateNumber:
        registrationDeliveryDetails?.firstRegistrationDetails?.licensePlateNumber ?? undefined,
      registrationStatus:
        registrationDeliveryDetails?.firstRegistrationDetails?.registrationStatus ?? '',
      scheduledDeliveryDate:
        registrationDeliveryDetails?.scheduledDeliveryDetails?.scheduledDeliveryDate ?? undefined,
      scheduledDeliveryTime:
        registrationDeliveryDetails?.scheduledDeliveryDetails?.scheduledDeliveryTime ?? undefined,
      licensePlateIssuanceCode:
        registrationDeliveryDetails?.firstRegistrationDetails?.licensePlateIssuanceCode ??
        undefined,
      registrationCode1:
        registrationDeliveryDetails?.firstRegistrationDetails?.registrationCode1 ?? undefined,
      registrationCode2:
        registrationDeliveryDetails?.firstRegistrationDetails?.registrationCode2 ?? undefined,
      registrationOrderOtt:
        registrationDeliveryDetails?.firstRegistrationDetails?.registrationOrderOtt ?? undefined,
    };

    const vehicleReadyDate = vehicleEtaAtDeliveryCenter || undefined;

    return createRegistrationFormFields(labels, defaultValues, vehicleReadyDate, isNL);
  }, [labels, registrationDeliveryDetails, vehicleEtaAtDeliveryCenter, isNL]);
  const vehicleEtaAtDeliveryCenterDateValue = vehicleEtaAtDeliveryCenter
    ? formatDate(vehicleEtaAtDeliveryCenter, locale, {}, language ?? undefined)
    : '-';

  const handleSuccess = useCallback(
    (data: unknown) => {
      const submitForm = async () => {
        try {
          setErrorMessage(null);
          setSuccessMessage(null);
          // Clear any existing timeout
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = null;
          }
          setIsPending(true);

          if (!agencyPdiRoadworthinessCheckDateValue) {
            setErrorMessage(
              labels?.['agencyPdiRoadworthinessCheck.required'] ??
                'Agency PDI / Roadworthiness Check is required'
            );
            setIsPending(false);
            return;
          }

          // Type assertion - trust that the form library provides correctly typed data
          const formData = data as RegistrationFormDefaultValues;

          // Prepare PATCH request payload using spread - apiKey names match DTO field names
          const patchData = {
            orderIdentifier: orderData.orderIdentifier,
            registrationDetailsPreDelivery: {
              vehicleReadiness: {
                agencyPdiRoadworthinessCheckDate: agencyPdiRoadworthinessCheckDateValue,
              },
              firstRegistrationDetails: {
                firstRegistrationDate: formData.firstRegistrationDate,
                licensePlateNumber: formData.licensePlateNumber,
                registrationStatus: formData.registrationStatus,
                licensePlateIssuanceCode: formData.licensePlateIssuanceCode,
                registrationCode1: formData.registrationCode1,
                registrationCode2: formData.registrationCode2,
                registrationOrderOtt: formData.registrationOrderOtt,
              },
              scheduledDeliveryDates: {
                scheduledDeliveryDate: formData.scheduledDeliveryDate,
                scheduledDeliveryTime: formData.scheduledDeliveryTime || undefined,
              },
            },
          };

          await patchOrderDetailsMutation.mutateAsync(patchData);

          setSuccessMessage(
            labels?.['success.save'] ?? 'Registration details updated successfully'
          );

          // Clear previous timeout if exists
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
          }

          // Auto-hide success message
          successTimeoutRef.current = setTimeout(() => {
            setSuccessMessage(null);
          }, SUCCESS_MESSAGE_DURATION_MS);
        } catch (error: unknown) {
          // Extract error message from API response
          let errorMsg = labels?.['error.save'] ?? DEFAULT_SAVE_ERROR_MESSAGE;

          if (error && typeof error === 'object') {
            const apiError = error as {
              message?: string;
              response?: {
                data?: {
                  message?: string;
                  errors?: string[];
                };
              };
            };

            // Priority order: response.data.message > response.data.errors[0] > error.message
            if (apiError.response?.data?.message) {
              errorMsg = apiError.response.data.message;
            } else if (
              apiError.response?.data?.errors &&
              apiError.response.data.errors.length > 0
            ) {
              errorMsg = apiError.response.data.errors.join(', ');
            } else if (apiError.message && apiError.message !== 'Network Error') {
              errorMsg = apiError.message;
            }
          }

          // Apply localized error message translation using the active locale's labels
          errorMsg = getLocalizedErrorMessage(errorMsg, labels, language, locale);

          setErrorMessage(errorMsg);

          // Clear previous timeout if exists
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
          }

          // Auto-hide error message
          errorTimeoutRef.current = setTimeout(() => {
            setErrorMessage(null);
          }, SUCCESS_MESSAGE_DURATION_MS);
        } finally {
          setIsPending(false);
        }
      };

      void submitForm();
    },
    [
      agencyPdiRoadworthinessCheckDateValue,
      orderData.orderIdentifier,
      patchOrderDetailsMutation,
      labels,
      language,
      locale,
    ]
  );

  const handleError = useCallback(() => {
    setErrorMessage(labels?.['error.validation'] ?? 'Please correct the errors in the form');
    setSuccessMessage(null);

    // Clear previous timeout if exists
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Auto-hide validation error message
    errorTimeoutRef.current = setTimeout(() => {
      setErrorMessage(null);
    }, SUCCESS_MESSAGE_DURATION_MS);
  }, [labels]);

  return (
    <Box data-testid={dataTestId}>
      {/* Vehicle Readiness Section */}
      <Box mb={12}>
        <Typography variant="body" size="large" theme={theme} color="black">
          {labels?.['vehicle.readiness'] ?? 'Vehicle Readiness'}
        </Typography>
      </Box>
      <Box className={styles.readinessContainer} mb={24}>
        <Row gutter={[24, 24]}>
          <Col xs={12} sm={6} lg={6}>
            <DatePicker
              name="agencyPdiRoadworthinessCheckDate"
              label={
                labels?.['agencyPdiRoadworthinessCheck.label'] ??
                'Agency PDI / Roadworthiness Check'
              }
              theme={theme === 'dark' ? 'light' : 'dark'}
              locale={
                languageBasedLocale as
                  | 'en-US'
                  | 'en-CA'
                  | 'fr-CA'
                  | 'de-DE'
                  | 'nl-NL'
                  | 'de-CH'
                  | 'fr-CH'
                  | 'nb-NO'
                  | 'en-EU'
                  | 'en-AE'
                  | 'ar-AE'
                  | 'en-SA'
                  | 'ar-SA'
                  | 'fr-BE'
              }
              language={language ?? undefined}
              required
              value={agencyPdiRoadworthinessCheckDateValue || null}
              onChange={(date: Date) => {
                setAgencyPdiRoadworthinessCheckDateValue(formatToUTCDate(date));
                setErrorMessage(null);
              }}
            />
          </Col>

          <Col xs={12} sm={6} lg={6}>
            <Row gutter={[8, 0]}>
              <Col lg={12}>
                <Box
                  className={styles.labelWithTooltip}
                  data-testid="vehicle-ready-tooltip-trigger"
                >
                  <Typography variant="label" size="small" theme={theme} color="gray-7">
                    {labels?.['vehicleEtaAtDeliveryCenter.date'] ??
                      'Vehicle ETA at Delivery Center'}
                  </Typography>
                  <Tooltip
                    content={
                      labels?.['vehicleEtaAtDeliveryCenter.tooltip'] ??
                      labels?.['vehicleReadyToSchedule.tooltip'] ??
                      'If populated, the vehicle has no open Work Orders or Service Campaigns and should be delivery-ready (excluding Detailing). Auto-populated and owned by Service, required for Final Quality Check.'
                    }
                    side="bottom"
                    theme={theme === 'dark' ? 'light' : 'dark'}
                    dataTestId={`${dataTestId}-vehicle-ready-tooltip`}
                  >
                    <span
                      className={styles.tooltipIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      role="img"
                      aria-label={labels?.['tooltip.moreInformation'] ?? 'More information'}
                    >
                      <Iconography
                        category="global"
                        theme={theme}
                        name="information"
                        color="gray-5"
                      />
                    </span>
                  </Tooltip>
                </Box>
              </Col>
              <Col lg={12}>
                <Typography variant="body" size="small" theme={theme} color="black">
                  {vehicleEtaAtDeliveryCenterDateValue}
                </Typography>
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>

      {/* Messages */}
      {successMessage && <StatusMessage message={successMessage} type="success" theme={theme} />}
      {errorMessage && <StatusMessage message={errorMessage} type="error" theme={theme} />}

      {/* Loading Overlay */}
      {isPending && (
        <Box className={styles.fetchingOverlay}>
          <Loader size={32} theme={theme} />
        </Box>
      )}

      {/* Registration and Delivery Form */}
      <Box className={styles.formContainer}>
        <Form
          key={`registration-form-${orderData.orderIdentifier}-${JSON.stringify(registrationDeliveryDetails)}`}
          id="registration-details-form"
          ref={formRef}
          theme={theme === 'dark' ? 'light' : 'dark'}
          formFields={formFields}
          onSuccess={handleSuccess}
          onError={handleError}
          isFluid={true}
          locale={languageBasedLocale}
          language={language ?? undefined}
        />
      </Box>
    </Box>
  );
};

Registration.displayName = 'Registration';

export default Registration;
